import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import api from '../api';

const PiAppsDiscoveryContext = createContext(null);

const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

export function PiAppsDiscoveryProvider({ children, user }) {
  const [topApps, setTopApps] = useState([]);
  const [topAppsLoading, setTopAppsLoading] = useState(false);
  const [topAppsLoaded, setTopAppsLoaded] = useState(false);
  const [topAppsError, setTopAppsError] = useState(null);

  const fetchingRef = useRef(false);

  const normalizeAppsResponse = (data) => {
    if (Array.isArray(data?.apps)) return data.apps;
    if (Array.isArray(data)) return data;
    return [];
  };

  const buildTopAppsUrl = useCallback(() => {
    const viewer = user?.username ? String(user.username).trim() : '';

    if (!viewer) {
      return '/apps/discovery/top';
    }

    return `/apps/discovery/top?viewer=${encodeURIComponent(viewer)}`;
  }, [user?.username]);

  const fetchTopApps = useCallback(
    async ({ silent = false } = {}) => {
      if (fetchingRef.current) return;

      fetchingRef.current = true;

      if (!silent) {
        setTopAppsLoading(true);
      }

      try {
        const res = await api.get(buildTopAppsUrl());

        const apps = normalizeAppsResponse(res.data);

        setTopApps(apps);
        setTopAppsError(null);
        setTopAppsLoaded(true);
      } catch (err) {
        console.error('Failed to fetch top Pi Apps:', err);
        setTopAppsError(err);

        // Keep old apps if refresh fails.
        setTopAppsLoaded(true);
      } finally {
        fetchingRef.current = false;

        if (!silent) {
          setTopAppsLoading(false);
        }
      }
    },
    [buildTopAppsUrl]
  );

  const refreshTopApps = useCallback(() => {
    return fetchTopApps({ silent: true });
  }, [fetchTopApps]);

  const updateTopAppFollowState = useCallback((username, nowFollowing) => {
    const targetUsername = String(username || '').trim().toLowerCase();

    if (!targetUsername) return;

    setTopApps((prev) =>
      prev.map((app) => {
        const appUsername = String(app?.username || '').trim().toLowerCase();

        if (appUsername !== targetUsername) {
          return app;
        }

        const currentCount = Number(app.followersCount || 0);
        const wasFollowing = Boolean(app.isFollowing);

        let nextCount = currentCount;

        if (nowFollowing && !wasFollowing) {
          nextCount = currentCount + 1;
        }

        if (!nowFollowing && wasFollowing) {
          nextCount = Math.max(0, currentCount - 1);
        }

        return {
          ...app,
          isFollowing: Boolean(nowFollowing),
          followersCount: nextCount,
        };
      })
    );
  }, []);

  useEffect(() => {
    fetchTopApps({ silent: false });

    const intervalId = setInterval(() => {
      fetchTopApps({ silent: true });
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [fetchTopApps]);

  const value = {
    topApps,
    topAppsLoading,
    topAppsLoaded,
    topAppsError,
    fetchTopApps,
    refreshTopApps,
    updateTopAppFollowState,
  };

  return (
    <PiAppsDiscoveryContext.Provider value={value}>
      {children}
    </PiAppsDiscoveryContext.Provider>
  );
}

export function usePiAppsDiscovery() {
  const context = useContext(PiAppsDiscoveryContext);

  if (!context) {
    throw new Error(
      'usePiAppsDiscovery must be used inside PiAppsDiscoveryProvider'
    );
  }

  return context;
}
